import {
  CallIcon,
  CompletedIcon,
  CrossIcon,
  PendingIcon,
  CalenderIcon,
  EmailIcon,
} from "../../../images/icons/icons";
import { convertThenFormat, formatDateTime } from "../../common-data/Converts";

export const formatDate = (dateString) => {
  if (!dateString) return { date: "-", time: "-" };
  const date = new Date(dateString);
  return {
    date: date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    }),
    time: date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }),
  };
};

const isDateString = (s) => {
  if (typeof s !== "string" || s.trim() === "") return false;
  if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return false;
  const d = new Date(s);
  return d instanceof Date && !isNaN(d);
};

export const groupEventsByDate = (events) => {
  if (!events) return {};
  const groups = {};
  const today = new Date();
  events.forEach((event) => {
    const eventDate = new Date(event.date_added);
    let groupKey;
    if (
      eventDate.getDate() === today.getDate() &&
      eventDate.getMonth() === today.getMonth() &&
      eventDate.getFullYear() === today.getFullYear()
    ) {
      groupKey = "Today";
    } else {
      groupKey = eventDate.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      });
    }
    if (!groups[groupKey]) groups[groupKey] = [];
    groups[groupKey].push(event);
  });
  return groups;
};

export const getEventTypeDisplay = (eventType) => {
  return eventType.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

export const getIcon = (eventType) => {
  switch (eventType) {
    case "lead_created":
      return <CallIcon />;
    case "lead_updated":
      return <EmailIcon />;
    case "followup_added":
      return <CalenderIcon />;
    case "lead_assigned":
    case "converted":
      return <CompletedIcon />;
    case "duplicate_lead":
      return <CrossIcon />;
    default:
      return <PendingIcon />;
  }
};

const ignoredFields = [
  "gym_id",
  "raw_lead_id",
  "last_child_container_id",
  "customer_intent_id",
  "tn_lead_id",
  "btl_lead_id",
  "odm_lead_id",
  "walkin_lead_id",
  "remarks",
  "wap_image",
  "assigned_to",
  "name",
  "email",
  "mobile",
  "is_walkin_lead",
  "raw_lead",
  "lead_value",
  "is_odm_lead",
  "lead_type",
  "plan_type",
  "expected_closure_date",
  "uid",
  "lead_id",
  "data",
  "user_id",
  "last_child_container_id",
  "partial_payment_details",
  "requested_by",
  "added_by",
  "created_by",
  "plan_id",
  "status",
  "is_lead",
  "otp",
  "n_token",
  "referral_code",
  "account_type",
  "event_type",
  "subscription_id",
  "followUpDetails",
  "untr_wati_lead_id",
  "transferred_to",
  "transferred_from",
];

const formatValue = (key, value) => {
  if (key === "lead_status") {
    const status = String(value).toLowerCase();
    const formattedStatus = status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());

    if (status === "pending") {
      return <span className="lead-v2-status-pending">{formattedStatus}</span>;
    }
    if (status === "converted" || status === "closed") {
      return (
        <span className="lead-v2-status-converted">{formattedStatus}</span>
      );
    }
    return <span className="lead-v2-status-other">{formattedStatus}</span>;
  }

  if (key === "lead_value") {
    return `₹${convertThenFormat(value || 0)}`;
  }

  if (key === "lead_from") {
    if (String(value).toLowerCase() === "lms") return "LMS";
  }

  if (value === true || value === 1 || value === "1") {
    return <CompletedIcon />;
  }

  if (value === false || value === 0 || value === "0") {
    return <CrossIcon />;
  }

  if (isDateString(value)) {
    const formatDate = formatDateTime(value);
    return formatDate;
  }

  if (typeof value === "string") {
    return value.replace(/_/g, " ");
  }

  return String(value);
};

const getDisplayLabel = (key) => {
  const labelMap = {
    is_untr_wati_lead: "Untracked Wati Lead",
  };

  return labelMap[key] || key?.replace(/_/g, " ");
};

export const renderEventDetails = (event) => {
  const renderReqBody = (reqBody) => {
    if (!reqBody) return null;

    const entries = Object.entries(reqBody).filter(
      ([key, value]) =>
        !ignoredFields.includes(key) && value !== "" && value !== null
    );

    if (entries.length === 0) return null;

    return (
      <div className="lead-v2-req-body">
        <h6 className="lead-v2-req-body-title">Event Data</h6>

        <div className="lead-v2-req-body-grid">
          {entries.map(([key, value]) => (
            <div key={key} className="lead-v2-req-body-item">
              <span className="lead-v2-req-body-key">
                {getDisplayLabel(key)}
              </span>
              <span className="lead-v2-req-body-value">
                {formatValue(key, value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="lead-v2-event-details">
      {/* {event.remarks && (
        <p className="lead-v2-remarks">
          <strong>Remarks:</strong> {event.remarks}
        </p>
      )} */}

      {renderReqBody(event.req_body)}

      <p className="lead-v2-created-by">
        Added by {event.created_by_name} on{" "}
        {event.date_added ? formatDateTime(event.date_added) : "-"}
      </p>
    </div>
  );
};

export const renderCycleDetails = (cycle) => {
  const ignoredFields = [
    "child_container_id",
    "subscription_id",
    "lead_id",
    "added_by",
    "assigned_to",
    "req_body",
    "data",
    "screen_data",
    "screen",
    "is_pt_link",
    "payment_id",
    "gym_id",
    "user_id",
    "created_by",
    "customer_intent_id",
    "requested_by",
    "partial_payment_details",
    "date_added",
    "converted_date",
    "status",
  ];

  const entries = Object.entries(cycle).filter(
    ([key, value]) =>
      !ignoredFields.includes(key) && value !== "" && value !== null
  );

  return (
    <div className="lead-v2-cycle-details">
      <div className="lead-v2-cycle-grid">
        {entries.map(([key, value]) => (
          <div key={key} className="lead-v2-cycle-item">
            <span className="lead-v2-cycle-key">{key.replace(/_/g, " ")}</span>
            <span className="lead-v2-cycle-value">
              {formatValue(key, value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const formatValueCycle = (key, value) => {
  if (key === "lead_status" || key === "container_status") {
    const status = String(value).toLowerCase();
    const formattedStatus = status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());

    if (status === "pending") {
      return <span className="lead-v2-status-pending">{formattedStatus}</span>;
    }
    if (status === "converted" || status === "conveted") {
      return (
        <span className="lead-v2-status-converted">{formattedStatus}</span>
      );
    }
    return <span className="lead-v2-status-other">{formattedStatus}</span>;
  }

  if (key === "lead_source" || key === "source_name") {
    return (
      <span className="lead-v2-source-tag">
        {String(value).replace(/_/g, " ")}
      </span>
    );
  }

  if (
    key === "converted_value" ||
    key === "lead_value" ||
    key === "plan_price"
  ) {
    return `₹${convertThenFormat(value || 0)}`;
  }

  if (
    key === "date_added" ||
    key === "last_updated" ||
    key === "converted_date"
  ) {
    return formatDateTime(value);
  }

  if (typeof value === "string") {
    return value.replace(/_/g, " ");
  }

  return String(value);
};
